// src/v2/pages/OperationalPanel/home/MailSection.tsx
import { Alert, Box, Stack, Typography } from "@mui/material";

import { formatSaoPauloDateTime } from "../dates";
import { IMailStatus } from "../contracts/mail.types";
import { HOME_TOKENS } from "./tokens";

const plural = (n: number, um: string, muitos: string) => `${n} ${n === 1 ? um : muitos}`;

/**
 * Seccao "E-mails" — sinal de e-mails falhados (spec 2026-09-21). Irma da
 * `RondaSection`, e pelo mesmo motivo fora do union `CnpjState`.
 *
 * invariant: vermelho a primeira falha tecnica. Falso positivo custa um olhar
 * de manha; falso negativo custou dois dias de e-mails perdidos em 19-21/09.
 * hazard: a palavra e sempre "aceite pelo servidor". O SMTP nao sabe se
 * chegou, e a tela nao pode afirmar o que a plataforma nao sabe.
 */
export function MailSection({
  status,
  isError = false,
  alarmarSilencio = true,
}: {
  status: IMailStatus | undefined;
  isError?: boolean;
  /**
   * invariant: silencio e um invariante de carteira, nao de CNPJ. Um unico
   * cliente sem enviar nada hoje e normal (pode nao ter havido movimentacao
   * pra ele); so alarma quando olhado no agregado da tela inicial. A
   * `CnpjPage` passa `false` por isso — evita falso alarme por cliente.
   */
  alarmarSilencio?: boolean;
}) {
  if (!status && !isError) return null;

  return (
    <Box sx={{ mt: `${HOME_TOKENS.space.betweenZones}px` }}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, display: "block", mb: `${HOME_TOKENS.space.withinRow}px` }}>
        E-mails (últimas 24 h)
      </Typography>

      {status?.webhookSilent && (
        <Alert severity="error" data-severity="error" sx={{ mb: `${HOME_TOKENS.space.withinRow}px` }}>
          A plataforma não recebeu nenhum evento de entrega em 24 h, apesar dos envios. Sem isso, um e-mail devolvido não aparece
          em lugar nenhum. Confira o Event Webhook e a chave no servidor.
        </Alert>
      )}

      {(isError || !status) && (
        <Alert severity="warning" data-severity="warning" sx={{ mb: `${HOME_TOKENS.space.withinRow}px` }}>
          {/* why: mesmo padrao que `RondaSection` fixou — o alerta sozinho nao
              basta quando ha dado em cache: sem dizer a idade, o operador le
              a lista abaixo como estado atual. Sem cache, so ha o aviso curto. */}
          {status
            ? `Não foi possível ler o sinal de e-mails. O que aparece abaixo é o dado de ${formatSaoPauloDateTime(status.generatedAt)} e pode estar desatualizado; nunca leia como "tudo certo".`
            : 'Não foi possível ler o sinal de e-mails. Nunca leia a ausência desta seção como "tudo certo".'}
        </Alert>
      )}

      {status && status.technicalFailures.length > 0 && (
        <Alert severity="error" data-severity="error" sx={{ mb: `${HOME_TOKENS.space.withinRow}px` }}>
          <Stack spacing={0.5}>
            {status.technicalFailures.map((g) => (
              <Typography key={g.errorClass} variant="body2">
                {plural(g.count, "envio falhou", "envios falharam")} desde {formatSaoPauloDateTime(g.firstAt)}: <code>{g.errorClass}</code> em {plural(g.flows.length, "fluxo", "fluxos")} ({g.flows.join(", ")}) e {g.distinctCnpjs === 0 ? "sem CNPJ identificado" : plural(g.distinctCnpjs, "CNPJ", "CNPJs")}.
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {/* why: recusa de destinatario e uma causa DIFERENTE da falha tecnica —
          uma nao explica a outra, entao as duas aparecem juntas quando
          coexistem (vermelho primeiro, DOM ja garante a ordem). */}
      {status && status.recipientRejections.length > 0 && (
        <Alert severity="warning" data-severity="warning" sx={{ mb: `${HOME_TOKENS.space.withinRow}px` }}>
          <Stack spacing={0.5}>
            {status.recipientRejections.map((r) => (
              <Typography key={r.recipient} variant="body2">
                {plural(r.count, "recusa", "recusas")} por endereço inválido no cadastro: <code>{r.recipient}</code> ({r.flows.join(", ")}).
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {/* invariant: silencio so significa algo quando nao ha falha nem
          recusa para reportar — senao o aviso amarelo de silencio contradiz
          o alerta vermelho/amarelo que ja diz o motivo real do buraco. */}
      {status && status.silence && alarmarSilencio && status.technicalFailures.length === 0 && status.recipientRejections.length === 0 && (
        <Alert severity="warning" data-severity="warning" sx={{ mb: `${HOME_TOKENS.space.withinRow}px` }}>
          Nenhum e-mail enviado nas últimas 24 h num dia útil. A plataforma envia todos os dias; ausência de sinal é alarme.
        </Alert>
      )}

      {/* why: a linha neutra aparece sempre que o alerta de silencio NAO
          aparece — inclusive silencio real com `alarmarSilencio={false}` (a
          CnpjPage): "0 e-mails aceites" e verdade e precisa continuar visivel. */}
      {status && !(status.silence && alarmarSilencio) && status.technicalFailures.length === 0 && status.recipientRejections.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {plural(status.accepted, "e-mail aceite pelo servidor", "e-mails aceites pelo servidor")}
          {status.lastAcceptedAt ? `, último às ${formatSaoPauloDateTime(status.lastAcceptedAt)}.` : "."}
        </Typography>
      )}

      {status && status.unknownFlowCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: `${HOME_TOKENS.space.withinRow}px` }}>
          {status.unknownFlowCount} sem fluxo identificado (chamadores ainda sem contexto).
        </Typography>
      )}
    </Box>
  );
}
